<script setup lang="ts">
import type { IProps } from './FileItem.types'
import { useFileItem } from './FileItem.composable'

const { t } = useI18n()

const props = withDefaults(defineProps<IProps>(), {
  variant: 'card',
})

const {
  showFormat,
  showDate,
  showSize,
  fileUrl,
  title,
  date,
  format,
  size,
} = useFileItem(props)

const linkLabel = computed(() => props.options?.label || t('investor.files.downloadFile'))
const linkIcon = computed(() => props.options?.icon || '/icons/icn_arrow_right.svg')

</script>

<template>
  <div :class="['file-item', `file-item__variant-${props.variant}`]">
    <template v-if="props.variant === 'list'">
      <div class="file-item__content">
        <h4 class="file-item__title">
          {{ title }}
        </h4>

        <p
          v-if="showFormat && format"
          class="file-item__format"
        >
          {{ format }}
        </p>

        <p
          v-if="showSize && size"
          class="file-item__size"
        >
          {{ size }}
        </p>

        <p
          v-if="showDate && date"
          class="file-item__date"
        >
          {{ date }}
        </p>
      </div>

      <div class="file-item__actions">
        <a
          v-if="fileUrl"
          class="file-item__link"
          :href="fileUrl"
          target="_blank"
          rel="noopener noreferrer"
        >
          <p class="file-item__link-description">
            {{ linkLabel }}
          </p>

          <img
            class="file-item__link-icon"
            :src="linkIcon"
            aria-hidden="true"
          >
        </a>
      </div>
    </template>

    <template v-else>
      <p
        v-if="showFormat && format"
        class="file-item__format"
      >
        {{ format }}
      </p>

      <p
        v-if="showSize && size"
        class="file-item__size"
      >
        {{ size }}
      </p>

      <p
        v-if="showDate && date"
        class="file-item__date"
      >
        {{ date }}
      </p>

      <h4 class="file-item__title">
        {{ title }}
      </h4>

      <a
        v-if="fileUrl"
        class="file-item__link"
        :href="fileUrl"
        target="_blank"
        rel="noopener noreferrer"
      >
        <p class="file-item__link-description">
          {{ linkLabel }}
        </p>

        <img
          class="file-item__link-icon"
          :src="linkIcon"
          aria-hidden="true"
        >
      </a>
    </template>

  </div>
</template>

<style lang="scss" scoped>
.file-item {
  $root: &;
  display: block;
  height: 100%;
  padding: 3.2rem;
  background: $gray-100;
  border: 1px solid $base-50;
  border-radius: 1.2rem;

  &__date {
    @include body-3;
    display: inline-block;
    margin-right: 1.2rem;
    margin-bottom: 1.2rem;
    color: $text-300;
  }

  &__format {
    @include body-3;
    display: inline-block;
    margin-right: 1.2rem;
    margin-bottom: 1.2rem;
    padding: 0.4rem 1.2rem;
    color: $text-300;
    background-color: $gray-100;
    border: 1px solid $base-50;
    border-radius: 0.8rem;
  }

  &__size {
    @include body-3;
    display: inline-block;
    margin-right: 1.2rem;
    margin-bottom: 1.2rem;
    padding: 0.4rem 1.2rem;
    color: $text-300;
  }

  &__title {
    @include h10;
    margin-bottom: 1.6rem;
    color: $text-400;
  }

  &__link-description {
    @include body-3-medium;
    color: $text-300;
  }

  &__link {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1.2rem;
    width: fit-content;
    text-align: center;
    text-decoration: none;
    cursor: pointer;
  }

  &__variant-list {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding: 0.8rem;
    background: none;
    border: none;

    #{$root}__content {
      width: 100%;
      margin-bottom: 1.2rem;
    }

    #{$root}__actions {
      width: 100%;
    }

    #{$root}__title {
      @include body-2;
      margin-bottom: 0.8rem;
      color: $text-400;
    }

    #{$root}__format {
      @include body-4;
      margin-bottom: 0;
    }

    #{$root}__size {
      @include body-4;
      margin-bottom: 0;
    }

    #{$root}__link {
      width: 100%;
      padding: 1.4rem 2.4rem;
      border: 1px solid $gray-500;
      border-radius: 4.8rem;
    }

    @include atM {
      flex-direction: row;

      #{$root}__content {
        width: auto;
        margin-bottom: 0;
      }

      #{$root}__actions {
        width: auto;
      }

      #{$root}__link {
        width: auto;
      }
    }
  }
}
</style>
